import IconSearch from '@picsart/icons/IconSearch';
import IconMenu from '@picsart/icons/IconMenu';
import IconCrossLarge from '@picsart/icons/IconCrossLarge';
import IconChevronLeftLarge from '@picsart/icons/IconChevronLeftLarge';
import IconChevronDownSmall from '@picsart/icons/IconChevronDownSmall';
import classNames from 'classnames';
import { IconChevronRightSmall } from '@picsart/icons';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { ReactComponent as OpenInNewTab } from '@quicktools/shared-components/svgIcons/open-in-new-tab-icon.svg';

import NewLabel from '../../core/NewLabel';
import { COPYWRITER_TOOLS } from '../../../data/tools';
import { SingleToolSearch } from '../SingleToolSearch';
import { SingleToolAppIcon } from '../SingleToolAppIcon';

import useSingleToolNavigationHook from './useSingleToolNavigationHook';
import useStyles from './style';
import { ISingleToolNavbar, INavSubLink } from './types';
import SingleToolNavigationExtension from './SingleToolNavigationExtension';

function SingleToolNavigation({ items, analytics, pageName, toolName }: ISingleToolNavbar) {
  const classes = useStyles();

  const {
    mobileNavTitle,
    isMobileNavVisible,
    isSearchVisible,
    toggleNavigation,
    showSearch,
    isSearchActive,
    navToNextMenu,
    navToPrevMenu,
    currentMenu,
    currentCategory,
  } = useSingleToolNavigationHook();

  const handleOnGoToLink = (link: INavSubLink, hasSubCategories: boolean) => {
    handleAnalytics(link.category, link.appName);
    if (hasSubCategories) {
      navToNextMenu(link.linkLabel);
    }
  };

  const handleAnalytics = (category: string, appName: string) => {
    analytics.navigationClick({
      page_name: toolName === 'quicktools_homepage' ? 'homepage' : 'landing_page',
      category_name: category.toLowerCase(),
      action_type: appName.toLowerCase(),
    });
  };

  return (
    <>
      <div
        onClick={toggleNavigation}
        className={classNames(classes.backdrop, {
          isVisible: isMobileNavVisible,
        })}
      ></div>

      <IconMenu onClick={toggleNavigation} className={classes.burgerMenu} />

      <div
        className={classNames(classes.root, classes.mobile, {
          isVisible: isMobileNavVisible,
        })}
      >
        <div className={classes.navMobileTitle}>
          <div className={classes.navMobileTitleText}>
            {!currentCategory && !isSearchVisible && (
              <IconCrossLarge onClick={toggleNavigation} className={classes.icon} />
            )}
            {(currentCategory || isSearchVisible) && (
              <IconChevronLeftLarge onClick={navToPrevMenu} className={classes.icon} />
            )}
            <span>{mobileNavTitle}</span>
          </div>
          <IconSearch className={classes.icon} onClick={showSearch} />
        </div>

        {isSearchVisible ? (
          <div className={classNames(classes.searchContainer)}>
            <SingleToolSearch
              analytics={analytics}
              className={classNames(classes.search, isSearchActive && classes.searchActive)}
              pageName={pageName}
              toolName={toolName}
            />
          </div>
        ) : (
          <nav className={classes.navContainer}>
            {items.map(
              ({
                label,
                subLinks,
                navLink,
                itemsCount,
                fillColor,
                iconColor,
                isNew,
                hasBullet,
              }) => {
                const lowerLabel = label.toLowerCase();
                const seeAllToolsHref = `/${lowerLabel}`;

                const sublinksToShow = isMobileNavVisible
                  ? subLinks
                  : subLinks.slice(0, itemsCount);
                if (!sublinksToShow.find((s) => s.href === seeAllToolsHref))
                  return (
                    <div key={label} className={classes.navbarItem}>
                      <div
                        className={classes.navbarItemLabel}
                        onClick={() => {
                          navToNextMenu(label);
                        }}
                      >
                        {isMobileNavVisible && <span className={classes.label}>{label}</span>}
                        {!isMobileNavVisible && (
                          <a
                            className={classes.label}
                            href={navLink}
                            onClick={() => handleAnalytics(label, label)}
                          >
                            {label}
                          </a>
                        )}
                        {hasBullet && <div className={classes.bullet} />}
                        <IconChevronDownSmall className={classes.navItemArrow} />
                        {isNew && <NewLabel className={classes.new} size="small" />}
                      </div>

                      <ul
                        className={classNames(classes.navbarSubItems, {
                          isMobileActive: currentCategory === label,
                        })}
                      >
                        {sublinksToShow.map(
                          ({
                            name,
                            href,
                            id,
                            buttonHref,
                            icon,
                            subCategories,
                            openInNewTab,
                            showLabelOnNavBar,
                            isNew: isNewTool,
                          }) => {
                            const copyWriterTool = COPYWRITER_TOOLS.find((t) => t.id == id);
                            const link = copyWriterTool ? buttonHref || href : href;
                            return (
                              <li key={id} className={classes.navbarSubItem}>
                                <a
                                  onClick={() =>
                                    handleOnGoToLink(
                                      {
                                        linkLabel: name,
                                        url: link,
                                        appName: id,
                                        category: label,
                                      },
                                      !!subCategories,
                                    )
                                  }
                                  href={subCategories && isMobileNavVisible ? null : link}
                                  className={classes.navbarSubItemLink}
                                  target={openInNewTab ? '_blank' : '_self'}
                                  rel="noreferrer"
                                >
                                  <div className={classes.iconContainer}>
                                    {icon && (
                                      <SingleToolAppIcon
                                        icon={
                                          icon.noColor
                                            ? {
                                                src: icon.src,
                                                width: icon.width,
                                                height: icon.height,
                                              }
                                            : {
                                                ...icon,
                                                fill: fillColor,
                                                background: iconColor,
                                              }
                                        }
                                        size={'small'}
                                      />
                                    )}
                                    {name}
                                  </div>
                                  <div className={classes.toolLabels}>
                                    {showLabelOnNavBar && isNewTool && <NewLabel size="small" />}
                                    {openInNewTab && showLabelOnNavBar && <OpenInNewTab />}
                                  </div>
                                  {subCategories && (
                                    <IconChevronRightSmall className={classes.subCategoryArrow} />
                                  )}
                                </a>

                                {subCategories && (
                                  <SingleToolNavigationExtension
                                    currentMenu={currentMenu}
                                    name={name}
                                    subCategories={subCategories}
                                    maxSize={sublinksToShow.length}
                                  />
                                )}
                              </li>
                            );
                          },
                        )}
                        <li key={lowerLabel} className={classes.lastItemContainer}>
                          <a
                            href={navLink}
                            className={classNames(classes.navbarSubItemLink, classes.lastItem)}
                          >
                            {`See all ${lowerLabel} tools`}
                          </a>
                        </li>
                      </ul>
                    </div>
                  );
              },
            )}
          </nav>
        )}
      </div>
    </>
  );
}

export default SingleToolNavigation;
